import { PROPERTY_ALERT_FLOOR } from '@common/enums';
import type {
  AccountNotificationPreference,
  InboxItem,
  ReportSubscription,
  UserNotificationDelivery,
  UserSettings,
} from '@common/types';

import { CURRENT_USER_ID } from './org-users.seed';

export const DEFAULT_USER_SETTINGS: UserSettings = {
  version: 1,
  workspace: {
    landingPage: 'dashboard',
    landingDashboardId: null,
    startupScope: 'last-used',
    startupScopeSelection: { kind: 'all' },
    startupDateRange: 'last-used',
    startupDateRangePreset: 'last30',
  },
  appearance: {
    density: 'comfortable',
    pageSize: 25,
    rememberSidebar: false,
  },
  responding: {
    signature: null,
    defaultTone: null,
  },
  tickets: {
    autoWatchCreated: true,
    autoWatchCommented: false,
    defaultListScope: 'all',
  },
  updatedAt: null,
};

export const DEFAULT_ACCOUNT_NOTIFICATION_PREFERENCE: Omit<
  AccountNotificationPreference,
  'userId' | 'accountId'
> = {
  reviewEmails: 'alerts',
  summaries: ['weekly'],
  alertThreshold: PROPERTY_ALERT_FLOOR,
};

export const DEFAULT_NOTIFICATION_DELIVERY: Omit<UserNotificationDelivery, 'userId'> = {
  combineAccounts: false,
  summaryContent: 'scores',
  summaryTime: '07:00',
  quietHoursEnabled: false,
  quietHoursFrom: '21:00',
  quietHoursTo: '07:00',
};

/**
 * Explicit rows for people other than the signed-in user, so the admin read-only view has real
 * variety to show. Everyone not listed here is on the defaults.
 */
export const ACCOUNT_NOTIFICATION_PREFERENCES_SEED: ReadonlyArray<AccountNotificationPreference> = [
  {
    userId: 'usr-sarah',
    accountId: 'sun-001',
    reviewEmails: 'all',
    summaries: ['daily', 'weekly'],
    alertThreshold: 3,
  },
  {
    userId: 'usr-sarah',
    accountId: 'sun-002',
    reviewEmails: 'off',
    summaries: ['monthly'],
    alertThreshold: PROPERTY_ALERT_FLOOR,
  },
  {
    userId: 'usr-james',
    accountId: 'sun-001',
    reviewEmails: 'alerts',
    summaries: [],
    alertThreshold: 4,
  },
];

const hoursAgo = (hours: number): string => new Date(Date.now() - hours * 3_600_000).toISOString();

/** The notification centre's items. Built at load so the relative times read as recent. */
export function buildInboxSeed(): ReadonlyArray<InboxItem> {
  return [
    {
      id: 'inbox-1',
      event: 'review.low-score',
      accountId: 'sun-001',
      heading: 'Low score at The Maslow Sandton',
      body: 'A guest rated their stay 2 of 5 and mentioned a slow check-in.',
      atIso: hoursAgo(0.4),
      link: '/reviews/manage',
      readAt: null,
    },
    {
      id: 'inbox-2',
      event: 'ticket.assigned',
      accountId: 'sun-002',
      heading: 'Ticket assigned to you',
      body: '"Air conditioning not cooling in 412" at Table Bay Hotel.',
      atIso: hoursAgo(2),
      link: '/service-tickets/manage',
      readAt: null,
    },
    {
      id: 'inbox-3',
      event: 'ticket.sla-breached',
      accountId: 'sun-004',
      heading: 'Ticket passed its resolution target',
      body: '"Pool towels unavailable" at Sun City Resort is 6 hours over target.',
      atIso: hoursAgo(5),
      link: '/service-tickets/manage',
      readAt: null,
    },
    {
      id: 'inbox-4',
      event: 'survey.response-spike',
      accountId: 'sun-003',
      heading: 'Survey responses are up',
      body: 'The Boardwalk received 3x its usual responses yesterday.',
      atIso: hoursAgo(20),
      link: '/reviews/manage',
      readAt: null,
    },
    {
      id: 'inbox-5',
      event: 'integration.failed',
      accountId: 'sun-005',
      heading: 'Booking.com stopped syncing',
      body: 'Wild Coast Sun has not received Booking.com reviews for 24 hours.',
      atIso: hoursAgo(30),
      link: null,
      readAt: null,
    },
    {
      id: 'inbox-6',
      event: 'review.low-score',
      accountId: 'sun-002',
      heading: 'Low score at Table Bay Hotel',
      body: 'A guest rated cleanliness 1 of 5.',
      atIso: hoursAgo(52),
      link: '/reviews/manage',
      readAt: null,
    },
  ];
}

/** Personal report subscriptions belonging to the signed-in user. */
export const PERSONAL_REPORT_SUBSCRIPTIONS_SEED: ReadonlyArray<ReportSubscription> = [
  {
    id: 'personal-sub-1',
    accountId: 'sun-001',
    reportId: 'report-comprehensive',
    reportName: 'Comprehensive Performance',
    cadence: 'weekly',
    format: 'pdf',
    recipientLabels: ['Only me'],
    nextRunIso: '2026-10-05T06:00:00.000Z',
    lastRunIso: '2026-09-28T06:00:00.000Z',
    lastRunStatus: 'sent',
    isEnabled: true,
    ownerUserId: CURRENT_USER_ID,
  },
];
